import { Bar } from "react-chartjs-2";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Box, CardContent, Link, Typography as T } from "@mui/material";
import { TypographyProps } from "@mui/material/Typography/Typography";
import { ChartOptions } from "chart.js";
import { QuestionDetails } from "catalog/types/interfaces/conceptResponse";
import { DEFAULT_OPTS } from "catalog/utils/chart";
import FlatCard from "~/components/FlatCard";
import ListCard, { ListItem } from "~/components/ListCard";
import { merge } from "chart.js/helpers";

const numFormatter = new Intl.NumberFormat("en");
const compactFormatter = new Intl.NumberFormat("en", { notation: "compact" });

const BAR_CHART_OPTS: ChartOptions<"bar"> = merge<ChartOptions<"bar">, ChartOptions<"bar">>(
  {
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        title: {
          display: true,
          text: "Response Count",
        },
        ticks: {
          callback: (value) => {
            if (!isNaN(Number(value))) {
              return compactFormatter.format(Number(value));
            }
            return value;
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        displayColors: false,
        axis: "x",
        intersect: false,
      },
    },
  },
  DEFAULT_OPTS
);

const ListItemLabel = (props: TypographyProps) => (
  <T variant="body1" fontWeight={500} flex={1} whiteSpace="pre" {...props} />
);

const QuestionDetail = ({
  data,
  showGroupLink = false,
}: {
  data: QuestionDetails;
  showGroupLink?: boolean;
}) => {
  const location = useLocation();
  const metadataList = [];

  if (data.response_count) {
    metadataList.push(
      <ListItem key="total-responses">
        <ListItemLabel>Total Responses</ListItemLabel>
        <T variant="body1" sx={{ opacity: 0.67 }}>
          {numFormatter.format(data.response_count)}
        </T>
      </ListItem>
    );
  }

  if (data.survey_year) {
    metadataList.push(
      <ListItem key="survey-year">
        <ListItemLabel>Survey Year</ListItemLabel>
        <T sx={{ opacity: 0.67 }}>{data.survey_year}</T>
      </ListItem>
    );
  }

  if (data.survey_url) {
    const filename = data.survey_url.split("/").at(-1);
    // Handle adding page anchor extension to skip to relevant text
    const pageAnchor = data.survey_page ? `#page=${data.survey_page}` : "";

    metadataList.push(
      <ListItem key="survey-link">
        <ListItemLabel>Question Source</ListItemLabel>
        <Link
          href={data.survey_url + pageAnchor}
          target="_blank"
          underline="hover"
          fontWeight={500}
        >
          {filename}
        </Link>
      </ListItem>
    );
  }

  if (data.column_mapping) {
    const { account_name, schema, table_name, column_name } = data.column_mapping;
    const tableFQN = ["snowflake", account_name, schema, table_name].join(".");
    const qs = `?field=${column_name}&exact=1`;

    metadataList.push(
      <ListItem key="dataset">
        <ListItemLabel>Catalog Source</ListItemLabel>
        <Link
          component={RouterLink}
          to={`/data-browser/tables/${tableFQN}${qs}`}
          state={{ referrer: location.pathname }}
          underline="hover"
          fontWeight={500}
        >
          {table_name}.{column_name}
        </Link>
      </ListItem>
    );
  }

  if (showGroupLink && data.question_group_name) {
    const questionGroupParentUrl = `/data-browser/dashboards/concepts/${data.parent_ids.at(
      -1
    )}/details`;
    metadataList.push(
      <ListItem key="group-name">
        <ListItemLabel>Grouped Under</ListItemLabel>
        <Link component={RouterLink} to={questionGroupParentUrl} underline="hover" fontWeight={500}>
          {data.question_group_name}
        </Link>
      </ListItem>
    );
  }

  return (
    <>
      <ListCard>{metadataList}</ListCard>
      {data.question_response_value_count_distribution && (
        <FlatCard>
          <CardContent>
            <T variant="body1" fontWeight={600}>
              Response Breakdown
            </T>
            <Box p={3} className="chart-container">
              <Bar
                data={{
                  labels: data.question_response_value_count_distribution.map(
                    ({ label, raw_value }) => label || raw_value
                  ),
                  datasets: [
                    {
                      label: "Response Count",
                      data: data.question_response_value_count_distribution.map(
                        ({ count }) => count
                      ),
                    },
                  ],
                }}
                options={BAR_CHART_OPTS}
              />
            </Box>
          </CardContent>
        </FlatCard>
      )}
    </>
  );
};

export default QuestionDetail;
